import { imageUtils } from './imageUtils'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'

interface FilterProcessingQueue {
  photoUrl: string
  photoId: string
  filterId: string
}

class BackgroundFilterProcessor {
  private queue: FilterProcessingQueue[] = []
  private isProcessing = false
  private maxConcurrent = 1 // Process 1 filter at a time for robustness

  async addToQueue(photoUrl: string, photoId: string, filterId: string): Promise<void> {
    this.queue.push({ photoUrl, photoId, filterId })

    if (!this.isProcessing) {
      this.processQueue()
    }
  }

  private async processQueue(): Promise<void> {
    if (this.queue.length === 0) {
      this.isProcessing = false
      return
    }

    this.isProcessing = true

    // Process items in batches
    const batch = this.queue.splice(0, this.maxConcurrent)

    const promises = batch.map(async (item) => {
      try {
        await this.processFilterForPhoto(item.photoUrl, item.photoId, item.filterId)
      } catch (error) {
        console.error(`Failed to process filter ${item.filterId} for photo ${item.photoId}:`, error)
      }
    })

    await Promise.all(promises)

    // Continue processing remaining items
    if (this.queue.length > 0) {
      setTimeout(() => this.processQueue(), 100) // Small delay to prevent blocking
    } else {
      this.isProcessing = false
    }
  }

  private async processFilterForPhoto(
    photoUrl: string,
    photoId: string,
    filterId: string
  ): Promise<void> {
    const { filters } = useConfigStore.getState()
    const filter = filters.find((f) => f._id === filterId)
    if (!filter) return

    try {
      // Read gallery once at start to get current item for processing
      const { gallery: initialGallery, setSessionState } = useSessionStateStore.getState()
      const currentItem = initialGallery.capturedItems?.find((item) => item.id === photoId)
      
      // Use originalPhotoUrl if available, otherwise use photoUrl
      // This ensures we always process the original unfiltered image
      const originalPhotoUrlToProcess = currentItem?.originalPhotoUrl || photoUrl
      
      // Apply filter to photo (supports both manual adjustments and LUT)
      const filteredPhotoUrl = await imageUtils.applyFilterToImage(
        originalPhotoUrlToProcess,
        filter
      )

      // Re-read fresh gallery state after async work to avoid clobbering concurrent updates
      const { gallery: freshGallery } = useSessionStateStore.getState()
      const updatedItems = (freshGallery.capturedItems || []).map((item) => {
        if (item.id === photoId) {
          return {
            ...item,
            // Store originalPhotoUrl if not already set (first time filtering this photo)
            originalPhotoUrl: item.originalPhotoUrl || photoUrl,
            filteredVersions: {
              ...(item.filteredVersions || {}),
              [filterId]: filteredPhotoUrl
            }
          }
        }
        return item
      })
      setSessionState({
        gallery: {
          capturedItems: updatedItems
        }
      })

      console.log(`✅ Filter ${filter.name} applied to photo ${photoId}`)
    } catch (error) {
      console.error(`Failed to apply filter ${filterId} to photo ${photoId}:`, error)
    }
  }

  // private async checkAndCreateGIF(filterId: string): Promise<void> {
  //   const { gallery, setSessionState } = useSessionStateStore.getState()
  //   const { filters } = useConfigStore.getState()

  //   const filter = filters.find((f) => f._id === filterId)
  //   if (!filter) return

  //   // Get all captured photos
  //   const allPhotos = gallery.capturedItems.filter((item) => item.photoUrl)

  //   // Check if all photos have this filter applied
  //   const filteredPhotos = allPhotos
  //     .map((item) => item.filteredVersions?.[filterId])
  //     .filter((url): url is string => Boolean(url))

  //   if (filteredPhotos.length === allPhotos.length && filteredPhotos.length > 0) {
  //     try {
  //       // All photos are filtered, create GIF
  //       const gifBlob = await imageUtils.createGIF(
  //         filteredPhotos,
  //         1000, // 1 second per frame
  //         undefined // No custom frame
  //       )
  //       const gifUrl = URL.createObjectURL(gifBlob)

  //       // Update filter cache
  //       setSessionState({
  //         gallery: {
  //           filterCache: {
  //             ...gallery.filterCache,
  //             [filterId]: {
  //               photos: filteredPhotos,
  //               gifUrl,
  //               isComplete: true
  //             }
  //           }
  //         }
  //       })
  //     } catch (error) {
  //       console.error(`Failed to create GIF for filter ${filterId}:`, error)
  //     }
  //   }
  // }

  async processAllFiltersForPhoto(photoUrl: string, photoId: string): Promise<void> {
    const { filters } = useConfigStore.getState()

    // Add all filters to queue for this photo
    for (const filter of filters) {
      await this.addToQueue(photoUrl, photoId, filter._id)
    }
  }

  getProcessingStatus(): { queueLength: number; isProcessing: boolean } {
    return {
      queueLength: this.queue.length,
      isProcessing: this.isProcessing
    }
  }

  clearQueue(): void {
    this.queue = []
    this.isProcessing = false
  }
}

export const backgroundFilterProcessor = new BackgroundFilterProcessor()
